// Libs
import { useMutation, useQuery } from '@apollo/client';
import React, { useRef, useState } from 'react';

// Queries
import {
  CREATE_ISSUE_TEMPLATE,
  CREATE_WORKFLOW_TEMPLATE,
  GET_ALL_MARKETS,
  GET_ALL_WORKFLOW_GLOBAL_TEMPLATES,
  UPDATE_WORKFLOW_TEMPLATE_HIDE,
  UPDATE_WORKFLOW_TEMPLATE,
  UPDATE_ISSUE_TEMPLATE
} from '../../api/queries/queries_local';

// Utils
import useCheckDocument from 'Shared/Utils/useCheckDocument';
import {
  SORTING_VARIANTS,
  VIEW_LIST_VARIANTS
} from '../InspectionTemplates/InspectionTypeActionbar';

// Transforms
import { iBinderHeaderEnum } from '@ibinder/ui-common-models';
import {
  useIBTranslation,
  useModalAction
} from '@ibinder/ui-common-hooks';
import { FILTERED_WORKFLOW_TEMPLATES } from '@ibinder/ui-common-utils';
import {
  getGloabalWorkflowTemplateHeaders,
  getGroupsFromAccessor,
  mapQueryToTableDataGlobalWorkflowTemplate,
  sortTemplates,
  transformGlobalInspectionTemplateList
} from '../InspectionTemplates/inspectionTypeTransformations';

import Actionbar from './ActionBar';
import CreateWorkflowTemplateSideSheet from './CreateWorkflowTemplateSideSheet';
import { getLabels } from './labels';
import { ModeType, TAB_TYPE_WORKFLOWS } from './transformation';
import GlobalTemplateTable from './GlobalTemplateTable';
// Styles

export enum FORM_MODES {
  CREATE = 'create',
  EDIT = 'edit',
  DUPLICATE = 'duplicate'
}

type WorkflowTemplate = {
  id: string;
  name: string;
  tenantSpecific: boolean;
};
type ListWorkFlowsProps = {
  // true: only the application tab templates (TAB_TYPE_WORKFLOWS), false: all other workflow templates
  tabTypesOnly?: boolean;
};

export const ListWorkFlows = ({ tabTypesOnly = false }: ListWorkFlowsProps) => {
  const { t } = useIBTranslation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const showCreateModalNode = useRef();
  const [formMode, setFormMode] = useState<FORM_MODES>();
  const createGlobalTypeTemplate = () => {
    openInspectionTemplateSideSheet();
  };

  const [showEditModal, setShowEditModal] = useState(false);
  const showEditModalNode = useRef();

  const [showViewModal, setShowViewModal] = useState(null);
  const showViewModalNode = useRef();

  const hideViewModal = () => {
    setShowViewModal(null);
  };

  const [activeView, setActiveView] = useState(VIEW_LIST_VARIANTS[0]);
  const changeActiveListView = (index) => {
    setActiveView(VIEW_LIST_VARIANTS[index]);
  };

  const marketInfo = useQuery(GET_ALL_MARKETS, {
    fetchPolicy: 'no-cache'
  });

  const workflowTemplates = useQuery(GET_ALL_WORKFLOW_GLOBAL_TEMPLATES, {
    fetchPolicy: 'no-cache',
    refetchWritePolicy: 'merge',
    notifyOnNetworkStatusChange: true,
  }
  );
  const templateList = workflowTemplates?.data?.get;
  const globalWorkflowTemplates = templateList?.filter(template => !FILTERED_WORKFLOW_TEMPLATES.includes(template.name));

  let workflowTemplateList = [];
  if(globalWorkflowTemplates){
    workflowTemplateList = [
      { Key: 'noTemplate', Value: t('noTemplate'),tenantSpecific: 'noTemplate' },
      ...globalWorkflowTemplates
        ?.map((templates) => ({
          Key: templates.id,
          Value: t(templates.name),
          tenantSpecific: templates.tenantSpecific
        }))
        .sort((a, b) => a.Value.localeCompare(b.Value))
    ];
  }
const onActionSelect = (action, itemId: string) => {
    switch (action) {
      case 'view':
        setFormMode(FORM_MODES.EDIT);
        openInspectionTemplateSideSheet({ id: itemId, mode: ModeType.Edit });
        return;
      case 'duplicate':
        setFormMode(FORM_MODES.DUPLICATE);
        setShowEditModal(true);
        return;
      case 'edit':
        setFormMode(FORM_MODES.EDIT);
        setShowEditModal(true);
        return;
    }
  };

  const [
    selectedIds,
    setSelectedIds,
    selectItem,
    selectItems,
    groupCheckStatus
  ] = useCheckDocument();

  const workflowMutationOptions = {
    refetchQueries: [{ query: GET_ALL_WORKFLOW_GLOBAL_TEMPLATES }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      workflowTemplates.refetch();
      setSelectedIds([]);
    },
    update: () => {
      workflowTemplates.refetch();
      setSelectedIds([]);
    },
  };
  const [updateWorkflowTemplateHide] = useMutation(UPDATE_WORKFLOW_TEMPLATE_HIDE, workflowMutationOptions);
  const [createIssueTemplate] = useMutation(CREATE_ISSUE_TEMPLATE);
  const [createWorkflowTemplate] = useMutation(CREATE_WORKFLOW_TEMPLATE, workflowMutationOptions);
  // edit workflow template
  const [updateWorkflowTemplate] = useMutation(UPDATE_WORKFLOW_TEMPLATE);
  const [updateIssueTemplate] = useMutation(UPDATE_ISSUE_TEMPLATE, workflowMutationOptions);

const handleWorkflowVisibility = async (itemId: string) => {
  try {
    await updateWorkflowTemplateHide({
      variables: {
        id: itemId, 
        body: {},
      },
      context: {
        headers: {
          [iBinderHeaderEnum.TENANTID]:
            "", 
        },
      },
    });
  } catch (error) {
    console.error("Error updating workflow template:", error);
  }
};

  const headers = getGloabalWorkflowTemplateHeaders(t, onActionSelect, workflowTemplateList, handleWorkflowVisibility);

  const editGlobalTypeTemplate = () => {
    setFormMode(FORM_MODES.EDIT);
    openInspectionTemplateSideSheet({ id: selectedIds[0], mode: ModeType.Edit });
  };

  const duplicateGlobalTypeTemplate = () => { 
    openInspectionTemplateSideSheet({ id: selectedIds[0], mode: ModeType.Duplicate });
  }
  const INVERTED_SORT_COLUMNS = ['createdBy', 'lastUpdatedBy'];
  const [sortingFilter, setSortingFilter] = useState(SORTING_VARIANTS.ASC);
  const [activeIndex, setActiveIndex] = useState(0);
  const [sortBy, setSortBy] = useState({ id: 'name', desc: false });

  const changeSorting = () => {
    if (sortingFilter === SORTING_VARIANTS.ASC) {
      setSortingFilter(SORTING_VARIANTS.DESC);
      setSortBy({ ...sortBy, desc: true });
    } else if (sortingFilter === SORTING_VARIANTS.DESC) {
      setSortingFilter(SORTING_VARIANTS.ASC);
      setSortBy({ ...sortBy, desc: false });
    }
  };

  const changeActiveSortField = (index) => {
    setActiveIndex(index);
    setSortingFilter(SORTING_VARIANTS.DESC);
    setSortBy({ id: headers[index].id, desc: true });
  };

  const onSort = (e) => {
    const index = headers.findIndex(({ id }) => id === e.id);
    setActiveIndex(index);
    setSortingFilter(
      e.desc === true ? SORTING_VARIANTS.DESC : SORTING_VARIANTS.ASC
    );
    setSortBy({ id: headers[index].id, desc: e.desc === true });
  };

  const sortedTemplates = workflowTemplates?.data
    ? sortTemplates(
        transformGlobalInspectionTemplateList(
          mapQueryToTableDataGlobalWorkflowTemplate(
            workflowTemplates?.data?.get.filter(
              (template) =>
                TAB_TYPE_WORKFLOWS.includes(template.name) === tabTypesOnly
            ),
            marketInfo?.data?.get,
            TAB_TYPE_WORKFLOWS
          )
        ),
        headers[activeIndex].propName,
        INVERTED_SORT_COLUMNS.includes(headers[activeIndex].id)
        ? sortingFilter === SORTING_VARIANTS.DESC
        : sortingFilter === SORTING_VARIANTS.ASC
      )
    : [];
    
  let isDefaultTemplate = false;
  if(selectedIds.length === 1) {
    const getSelectedTemplate = sortedTemplates.find((template => template.id === selectedIds[0]));
    isDefaultTemplate = getSelectedTemplate ? getSelectedTemplate?.name.toLowerCase().includes("default") : false;
  }

  const groupBy = {
    accessor: 'Category',
    groupByColumns: getGroupsFromAccessor(sortedTemplates, 'category')
  };
  
  const [WorkflowTemplateSideSheet, openInspectionTemplateSideSheet] =
    useModalAction({}, (props) => (
      <CreateWorkflowTemplateSideSheet
        createIssueTemplate={createIssueTemplate}
        createWorkflowTemplate={createWorkflowTemplate}
        updateWorkflowTemplate={updateWorkflowTemplate}
        updateIssueTemplate={updateIssueTemplate}
        marketInfo={marketInfo}
        {...{
          ...props,
          onClose: () => {
            props.onClose();
          },
          labels : getLabels(t),
          selectedId: props.data?.id,
          mode:props.data?.mode,
          t
        }}
      />
    ));
  return (
    <>
    {/* TODO: WILL IMPLEMENT LATER */}
      <Actionbar
        {...{
          selectedIds,
          createGlobalTypeTemplate,
          editGlobalTypeTemplate,
          duplicateGlobalTypeTemplate,
          labels: getLabels(t),
          isDefaultTemplate,
          canCreate: !tabTypesOnly
        }}
      />
      {sortedTemplates.length > 0 && (
        <GlobalTemplateTable
          {...{
            headers,
            queryData: sortedTemplates,
            selectedIds,
            setSelectedIds,
            selectItem,
            selectItems,
            groupCheckStatus,
            sortBy,
            onSort,
            groupBy
          }}
        />
      )}
      {WorkflowTemplateSideSheet}
    </>
  );
}